import { faCalendarCheck, faClock } from '@fortawesome/free-regular-svg-icons'
import { faCar, faCheck, faDollarSign, faStar, faTimes, faUsd } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import classNames from 'classnames'
import React, { createContext, FC, PropsWithChildren, ReactFragment, ReactNode, useContext } from 'react'
import { cn, extendedCn } from '../lib/utils'
import './Alert.css'

export enum alertTypes {
   ALLOTMENT = 'ALLOTMENT',
   RATES = 'RATES',
   VISITS = 'VISITS',
   PROMOTION = 'PROMOTION',
   REVIEWS = 'REVIEWS',
   GENERIC = 'GENERIC',
   GENERIC_SUCCESS = 'GENERIC_SUCCESS',
   GENERIC_WARNING = 'GENERIC_WARNING',
   GENERIC_DANGER = 'GENERIC_DANGER',
   GENERIC_INFO = 'GENERIC_INFO',
   FREE_CANCELLATION = 'FREE_CANCELLATION',
   FREE_TRANSFER = 'FREE_TRANSFER',
   BEST_PRICE_GUARANTEED = 'BEST_PRICE_GUARANTEED',
   NONE = 'NONE',
}

export type HotelAlert = {
   id: number
   alerts: Array<AlertType>
   rooms?: Array<AlertRoom>
}
export type AlertRoom = {
   id: number
   ratePlans: Array<AlertRatePlan>
}
export type AlertRatePlan = {
   id: number
   alerts: Array<{ text: string | null; type: string }>
}
export type AlertType = {
   title?: string
   subtitle?: string
   text?: JSX.Element | string | ReactNode
   type: alertTypes
}

type mode = 'light' | 'dark'
// sm: ícono a tamaño normal en vez de 2x y texto base de 14px
type size = 'md' | 'sm'

type Props = {
   type?: alertTypes
   isLoading?: boolean
   children?: ReactNode
   onDismiss?: Function
   mode?: mode
   className?: string
   clean?: boolean
   size?: size
}

type ContentProps = {
   className?: string
   containerClassName?: string
}

type AlertContextType = {
   type?: alertTypes
   mode?: mode
   onDismiss?: Function
   clean?: boolean
   size?: size
}

const AlertContext = createContext<AlertContextType>({} as AlertContextType)

const Body: FC<PropsWithChildren<ContentProps>> = ({ children, className, containerClassName }) => {
   const { onDismiss, mode, clean, size } = useContext(AlertContext)
   return (
      <div
         className={classNames(
            containerClassName,
            {
               'border-t border-b first:border-l last:border-r': mode === 'light',
               'p-3': !clean,
               'text-[14px]': size === 'sm',
            },
            'first:rounded-tl-[inherit] first:rounded-bl-[inherit] last:rounded-tr-[inherit] last:rounded-br-[inherit]',
            'itm-media-body basis-full flex items-center justify-between ',
         )}
      >
         <div className={className}>{children}</div>
         {onDismiss && (
            <FontAwesomeIcon
               icon={faTimes}
               className="cursor-pointer pl-2"
               onClick={() => {
                  onDismiss()
               }}
            />
         )}
      </div>
   )
}

const Icon: FC<PropsWithChildren<ContentProps>> = ({ children, className }) => {
   const { type, mode, size } = useContext(AlertContext)
   const iconSize = size === 'sm' ? undefined : '2x'
   function drawIcon() {
      switch (type) {
         case 'ALLOTMENT':
         case 'VISITS':
            return <FontAwesomeIcon icon={faClock} size={iconSize} fixedWidth />
         case 'PROMOTION':
         case 'RATES':
            return <FontAwesomeIcon icon={faUsd} size={iconSize} fixedWidth />
         case 'REVIEWS':
            return <FontAwesomeIcon icon={faStar} size={iconSize} fixedWidth />
         case 'FREE_CANCELLATION':
            return <FontAwesomeIcon icon={faCalendarCheck} size={iconSize} fixedWidth />
         case 'FREE_TRANSFER':
            return <FontAwesomeIcon icon={faCar} size={iconSize} fixedWidth />
         case 'BEST_PRICE_GUARANTEED':
            return <FontAwesomeIcon icon={faDollarSign} size={iconSize} fixedWidth />

         default:
            return <FontAwesomeIcon icon={faCheck} size={iconSize} fixedWidth />
      }
   }
   return (
      <div
         className={cn(
            {
               'border-t border-b first:border-l last:border-r': mode === 'light',
            },
            'first:rounded-tl-[inherit] first:rounded-bl-[inherit] last:rounded-tr-[inherit] last:rounded-br-[inherit]',
            'itm-media-left flex items-center p-3',
            className,
         )}
      >
         {children || drawIcon()}
      </div>
   )
}

const Alert = ({
   type = alertTypes.GENERIC,
   isLoading,
   children,
   onDismiss,
   mode,
   className,
   clean = false,
   size = 'md',
}: Props) => {
   function getNoticeStyle() {
      if (isLoading) {
         return 'itm-alert--spinning'
      }
      switch (type) {
         case 'ALLOTMENT':
         case 'VISITS':
         case 'GENERIC_DANGER':
            return 'itm-alert--danger'
         case 'PROMOTION':
         case 'RATES':
         case 'GENERIC_SUCCESS':
         case 'FREE_CANCELLATION':
         case 'FREE_TRANSFER':
         case 'BEST_PRICE_GUARANTEED':
            return 'itm-alert--success'
         case 'REVIEWS':
         case 'GENERIC_WARNING':
            return 'itm-alert--warning'
         case 'GENERIC_INFO':
            return 'itm-alert--info'
         case 'NONE':
            return ''
         default:
            return 'itm-alert--default'
      }
   }
   return (
      <div
         className={extendedCn({
            borderRadius: ['custom'],
         })(
            `flex items-stretch itm-alert sentry-unmask`,
            getNoticeStyle(),
            mode === 'light' ? 'itm-alert--light' : null,
            className,
         )}
      >
         <AlertContext.Provider value={{ type, mode, clean, onDismiss, size }}>{children}</AlertContext.Provider>
      </div>
   )
}

Alert.Body = Body
Alert.Icon = Icon

export default Alert
